/**
 * 由归一化人脸数据估算「完整人物」裁切矩形（相对视频 0~1）
 * mouthOpenness 等扩展字段会被忽略，仅使用 x / y / width / height
 * @module SpeakerDetection/utils/personCropFromFace
 */

/**
 * @typedef {Object} NormalizedFaceData
 * @property {number} x - 人脸框左上角 x（0~1）
 * @property {number} y - 人脸框左上角 y（0~1）
 * @property {number} width - 人脸框宽（0~1）
 * @property {number} height - 人脸框高（0~1）
 * @property {number} [mouthOpenness] - 可选，本方法不使用
 */

/**
 * @typedef {Object} PersonCropOptions
 * @property {number} [widthMultiplier=3.2] - 人物框宽度基准 ≈ 人脸宽 × 该值
 * @property {number} [heightMultiplier=4.5] - 人物框高度基准 ≈ 人脸高 × 该值
 * @property {number} [centerShiftYInFaceHeights=0.62] - 裁切框中心相对人脸中心向下移（× 人脸高度）；过大会把头顶裁掉
 * @property {number} [headClearanceInFaceHeights=0.4] - 尽量保证人脸框顶沿上方至少留出的高度（× 人脸高度），定稿后会把裁切框整体上移满足该留白
 * @property {number|string|{width:number,height:number}} [aspectRatio] - 目标裁切 **像素** 宽高比（宽/高），如输出画布 9:16 为 9/16；不传则沿用旧 clamp 逻辑
 * @property {number} [sourceAspectRatio] - 源视频帧 **像素** 宽/高（= frameWidth/frameHeight）。传入时会把 aspectRatio 转为归一化坐标系下的 w/h 比：normW/normH = (像素目标比) / sourceAspectRatio，避免在 16:9 源上等价成像素 1:1；不传则保持旧行为（aspectRatio 直接作为 norm 的 w/h）
 * @property {number} [smallFaceDiagRef=0.11] - 人脸 √(w²+h²) 大于约等于此值时不再额外收紧取景（0~1 归一化）
 * @property {number} [smallFaceDiagLo=0.026] - 与 ref 配合做插值下限，过小脸更偏「放大」
 * @property {number} [smallFaceTightenMinScale=0.48] - 极小人脸时 width/height 倍率乘子的下限（更小 = 取景更紧、画面放大更多）
 */

/**
 * @typedef {Object} DualCombinedCropOptions
 * @property {number} [headPadInFaceHeights=0.75] - 较上人脸顶沿向上的留白（× refFh），含头发
 * @property {number} [sidePadInFaceHeights=0.42] - 左右外扩（× refFh）
 * @property {number} [bodyPadBelowInFaceHeights=2.85] - 较低人脸底沿向下的留白（× refFh），含上身
 */

/**
 * 将矩形限制在 [0,1]×[0,1] 内；宽高可独立压到 1（不保持宽高比）
 */
function clampNormalizedRect(x, y, w, h) {
    let width = Math.min(Math.max(w, 1e-9), 1);
    let height = Math.min(Math.max(h, 1e-9), 1);
    const cx = x + w / 2;
    const cy = y + h / 2;
    let left = cx - width / 2;
    let top = cy - height / 2;
    left = Math.max(0, Math.min(1 - width, left));
    top = Math.max(0, Math.min(1 - height, top));
    return { x: left, y: top, width, height };
}

/**
 * @param {number|string|{width:number,height:number}} aspectRatio
 * @returns {number} 宽/高
 */
function parseAspectRatioWidthOverHeight(aspectRatio) {
    if (typeof aspectRatio === 'number') {
        if (!Number.isFinite(aspectRatio) || aspectRatio <= 0) {
            throw new Error('getPersonCropRectFromFaceData: aspectRatio 数字须为有限正数');
        }
        return aspectRatio;
    }
    if (typeof aspectRatio === 'string') {
        const parts = aspectRatio.split(':').map(s => parseFloat(s.trim()));
        if (parts.length !== 2 || !parts.every(n => Number.isFinite(n) && n > 0)) {
            throw new Error('getPersonCropRectFromFaceData: aspectRatio 字符串须为 "宽:高"');
        }
        return parts[0] / parts[1];
    }
    if (aspectRatio && typeof aspectRatio === 'object' && aspectRatio.width > 0 && aspectRatio.height > 0) {
        return aspectRatio.width / aspectRatio.height;
    }
    throw new Error(
        'getPersonCropRectFromFaceData: aspectRatio 须为大于 0 的数字、字符串如 "9:16" 或 { width, height }',
    );
}

/**
 * 以 (cx,cy) 为中心放置裁切框，保持宽高比；仅当 w/h 超出 [0,1]² 时整体缩小，**不会**为贴满源画面而放大取景
 * （避免小脸时被扩成近全幅，导致 cover 到画布后人像过小）
 */
function clampNormRectKeepAspectCentered(cx, cy, w, h) {
    let width = Math.max(w, 1e-9);
    let height = Math.max(h, 1e-9);
    const scaleDown = Math.min(1, 1 / width, 1 / height);
    width *= scaleDown;
    height *= scaleDown;
    let left = cx - width / 2;
    let top = cy - height / 2;
    left = Math.max(0, Math.min(1 - width, left));
    top = Math.max(0, Math.min(1 - height, top));
    return { x: left, y: top, width, height };
}

function expandRectToAspectRatio(cropW0, cropH0, ar) {
    const r0 = cropW0 / cropH0;
    if (r0 >= ar) {
        const width = cropW0;
        const height = width / ar;
        return { width, height };
    }
    const height = cropH0;
    const width = height * ar;
    return { width, height };
}

/**
 * 检测框上沿以上通常还有头发；若未满足「人脸顶沿上方至少 clearance×fh」留白，则将裁切框整体上移（不缩放）
 */
function nudgeCropRectUpForHeadRoom(rect, face, fh, clearanceInFaceHeights) {
    if (!rect || clearanceInFaceHeights <= 0) return rect;
    const limitTop = face.y - clearanceInFaceHeights * fh;
    const capTop = Math.max(0, limitTop);
    if (rect.y <= capTop + 1e-9) return rect;
    const y = Math.min(rect.y, capTop);
    return { ...rect, y: Math.max(0, y) };
}

/**
 * @param {NormalizedFaceData} face
 * @param {PersonCropOptions} [options]
 * @returns {{ x: number, y: number, width: number, height: number }}
 */
export function getPersonCropRectFromFaceData(face, options = {}) {
    if (
        !face ||
        typeof face.x !== 'number' ||
        typeof face.y !== 'number' ||
        typeof face.width !== 'number' ||
        typeof face.height !== 'number'
    ) {
        throw new Error('getPersonCropRectFromFaceData: face 需包含数字字段 x, y, width, height');
    }

    const {
        widthMultiplier = 3.2,
        heightMultiplier = 4.5,
        centerShiftYInFaceHeights = 0.62,
        headClearanceInFaceHeights = 0.4,
        aspectRatio: aspectRatioInput,
        sourceAspectRatio: sourceAspectRatioInput,
        smallFaceDiagRef = 0.11,
        smallFaceDiagLo = 0.026,
        smallFaceTightenMinScale = 0.48,
    } = options;

    const fw = Math.max(face.width, 1e-9);
    const fh = Math.max(face.height, 1e-9);

    const faceCenterX = face.x + fw / 2;
    const faceCenterY = face.y + fh / 2;

    const faceDiag = Math.hypot(fw, fh);
    let tightScale = 1;
    if (faceDiag < smallFaceDiagRef && smallFaceDiagRef > smallFaceDiagLo) {
        const t = Math.max(0, Math.min(1, (faceDiag - smallFaceDiagLo) / (smallFaceDiagRef - smallFaceDiagLo)));
        tightScale = smallFaceTightenMinScale + (1 - smallFaceTightenMinScale) * t;
    }

    const cropW0 = fw * widthMultiplier * tightScale;
    const cropH0 = fh * heightMultiplier * tightScale;

    const centerX = faceCenterX;
    const centerY = faceCenterY + centerShiftYInFaceHeights * fh;

    if (aspectRatioInput != null) {
        let ar = parseAspectRatioWidthOverHeight(aspectRatioInput);
        if (sourceAspectRatioInput != null && Number.isFinite(sourceAspectRatioInput) && sourceAspectRatioInput > 0) {
            ar = ar / sourceAspectRatioInput;
        }
        const { width: cropW, height: cropH } = expandRectToAspectRatio(cropW0, cropH0, ar);
        const rect = clampNormRectKeepAspectCentered(centerX, centerY, cropW, cropH);
        return nudgeCropRectUpForHeadRoom(rect, face, fh, headClearanceInFaceHeights);
    }

    const left = centerX - cropW0 / 2;
    const top = centerY - cropH0 / 2;
    const rect = clampNormalizedRect(left, top, cropW0, cropH0);
    return nudgeCropRectUpForHeadRoom(rect, face, fh, headClearanceInFaceHeights);
}

function assertNormalizedFaceForCrop(face, fnName) {
    if (
        !face ||
        typeof face.x !== 'number' ||
        typeof face.y !== 'number' ||
        typeof face.width !== 'number' ||
        typeof face.height !== 'number'
    ) {
        throw new Error(`${fnName}: face 需包含数字字段 x, y, width, height`);
    }
}

/**
 * 双人小脸联合取景：按「两人脸 + 头/身」估算轴对齐取景框（不强制目标宽高比，便于配合 FIT 完整入画）。
 * aspectRatio 参数保留以兼容调用，当前不参与计算。
 *
 * @param {NormalizedFaceData} faceA
 * @param {NormalizedFaceData} faceB
 * @param {number|string|{width:number,height:number}} [_aspectRatio]
 * @param {DualCombinedCropOptions} [options]
 * @returns {{ x: number, y: number, width: number, height: number }}
 */
export function getCombinedCropRectFromTwoFaceData(faceA, faceB, _aspectRatio, options = {}) {
    assertNormalizedFaceForCrop(faceA, 'getCombinedCropRectFromTwoFaceData');
    assertNormalizedFaceForCrop(faceB, 'getCombinedCropRectFromTwoFaceData');

    const { headPadInFaceHeights = 0.75, sidePadInFaceHeights = 0.42, bodyPadBelowInFaceHeights = 2.85 } = options;

    const fwA = Math.max(faceA.width, 1e-9);
    const fhA = Math.max(faceA.height, 1e-9);
    const fwB = Math.max(faceB.width, 1e-9);
    const fhB = Math.max(faceB.height, 1e-9);
    const refFh = Math.max(fhA, fhB);
    const sidePad = Math.max(sidePadInFaceHeights * refFh, 0.12 * Math.max(fwA, fwB));

    const x1 = Math.min(faceA.x, faceB.x);
    const y1 = Math.min(faceA.y, faceB.y);
    const x2 = Math.max(faceA.x + fwA, faceB.x + fwB);
    const y2 = Math.max(faceA.y + fhA, faceB.y + fhB);

    const uLeft = x1 - sidePad;
    const uTop = y1 - headPadInFaceHeights * refFh;
    const uRight = x2 + sidePad;
    const uBot = y2 + bodyPadBelowInFaceHeights * refFh;

    const x = Math.max(0, uLeft);
    const y = Math.max(0, uTop);
    const xr = Math.min(1, uRight);
    const yb = Math.min(1, uBot);
    const w = xr - x;
    const h = yb - y;

    if (w < 1e-5 || h < 1e-5) {
        throw new Error('getCombinedCropRectFromTwoFaceData: 联合取景区域过小');
    }

    return { x, y, width: w, height: h };
}
